import type { HandleDropTarget, HandleWaypointPlacement } from '@alltrails/maps/components/CustomRoute/DrawControls/DrawControlsProvider';
import type { RoutedHandle } from '@alltrails/maps/components/CustomRoute/DrawControls/useRouteToPoint';
import { getPointForHandle } from '@alltrails/maps/components/CustomRoute/DrawControls/validationUtils';
import {
  addWaypoint,
  clearWaypointEditingState,
  selectCustomRouteActiveSegment,
  setRouteWaypoints,
  setSegmentHandleStop
} from '@alltrails/maps/redux/customRoute';
import type { Coordinates } from '@alltrails/maps/types/Geo';
import { getHandleStop, getStopWaypointIds } from '@alltrails/maps/utils/customRoutes/pathStops';
import { getLinkedHandles } from '@alltrails/maps/utils/customRoutes/segmentLinking';
import { isWaypointAt, roundToWaypointPrecision } from '@alltrails/maps/utils/customRoutes/waypointPosition';
import { customRouteWaypointsId } from '@alltrails/maps/utils/layers';
import type { CustomRouteWaypointId } from '@alltrails/types/CustomRoute';
import { getNewWaypoint, startWaypointAdd } from '@/components/AdvancedBuilder/waypointAdd';
import type { AppDispatch, RootState } from '@/utils/redux';

// Pins the advanced builder's stops at route handles, and removes the waypoints it added for them once they are no longer stops.

type StopPlace = Pick<HandleDropTarget, 'coordinates' | 'name' | 'pin' | 'waypointCategory'>;

/**
 * The route waypoint a stop at a place is: the route waypoint pin it is on, or one already at exactly the place, as when a
 * place is added twice. Failing both, a waypoint added for the stop when the place is named, or none when it is not.
 */
const resolveStopWaypoint =
  ({ coordinates: { lat, lng }, name, pin, waypointCategory }: StopPlace) =>
  (dispatch: AppDispatch, getState: () => RootState): CustomRouteWaypointId | undefined => {
    const { route } = getState().customRoute;
    if (pin?.layerId === customRouteWaypointsId) {
      return route.waypoints.find(({ id }) => id === pin.id)?.id;
    }
    const existing = route.waypoints.find(waypoint => isWaypointAt(waypoint, lat, lng));
    if (existing) {
      return existing.id;
    }
    if (!name) {
      return undefined;
    }
    dispatch(
      addWaypoint({
        ...(route.contentPrivacy && { contentPrivacy: route.contentPrivacy }),
        description: '',
        isAddedForStop: true,
        latitude: roundToWaypointPrecision(lat),
        longitude: roundToWaypointPrecision(lng),
        name,
        showTitle: true,
        waypointCategory
      })
    );
    dispatch(clearWaypointEditingState());
    // addWaypoint puts the new waypoint last
    return getState().customRoute.route.waypoints.at(-1)!.id;
  };

/** Pins a stop at the handle a routing placed at a place, unless there is no waypoint for the place */
export const addStopWaypoint =
  ({ segmentIndex, handleIndex }: RoutedHandle, place: StopPlace) =>
  (dispatch: AppDispatch) => {
    const waypointId = dispatch(resolveStopWaypoint(place));
    if (waypointId !== undefined) {
      dispatch(setSegmentHandleStop({ segmentIndex, handleIndex, waypointId }));
    }
  };

/**
 * Whether the selected handle, which adding a stop routes from, is already at a place: on it exactly, or stopping at a route
 * waypoint there. Adding the place again would go nowhere, while adding it from any other handle is a return to it.
 */
export const selectIsSelectedHandleAt = (state: RootState, { lat, lng }: Coordinates) => {
  const activeSegment = selectCustomRouteActiveSegment(state);
  const { route, selectedHandleIndex } = state.customRoute;
  if (!activeSegment || selectedHandleIndex === undefined) {
    return false;
  }
  const point = getPointForHandle(activeSegment, selectedHandleIndex);
  if (point && isWaypointAt({ latitude: point[0], longitude: point[1] }, lat, lng)) {
    return true;
  }
  const stopId = getHandleStop(activeSegment.paths, selectedHandleIndex);
  const stop = stopId === undefined ? undefined : route.waypoints.find(({ id }) => id === stopId);
  return Boolean(stop && isWaypointAt(stop, lat, lng));
};

/**
 * After a handle of the active segment is dragged: it stops at the waypoint of the pin it was dropped on, or nowhere. A linked
 * neighbor's endpoint moved with it, so that stops at the same waypoint.
 */
export const moveHandleStop = (handleIndex: number, target?: HandleDropTarget) => (dispatch: AppDispatch, getState: () => RootState) => {
  const { activeSegmentIndex, route } = getState().customRoute;
  if (activeSegmentIndex === undefined || !route.segments[activeSegmentIndex]) {
    return;
  }
  const waypointId = target ? dispatch(resolveStopWaypoint(target)) : undefined;
  [{ segmentIndex: activeSegmentIndex, handleIndex }, ...getLinkedHandles(route.segments, activeSegmentIndex, handleIndex)].forEach(handle =>
    dispatch(setSegmentHandleStop({ ...handle, waypointId }))
  );
};

/**
 * A handle menu's Add waypoint: adds a waypoint at the handle, pinned there as its stop (and at a linked neighbor's
 * endpoint with it), and opens the editor on it as an add. Cancelling removes it, and with it the stop.
 */
export const createHandleWaypoint =
  ({ handleIndex, latitude, longitude, segmentIndex }: HandleWaypointPlacement) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    // Starting the add first, as any add does, so the new waypoint only selects itself, and the form starts over
    dispatch(startWaypointAdd());
    dispatch(addWaypoint(getNewWaypoint(roundToWaypointPrecision(latitude), roundToWaypointPrecision(longitude))));
    const { route, selectedWaypointId: waypointId } = getState().customRoute;
    [{ segmentIndex, handleIndex }, ...getLinkedHandles(route.segments, segmentIndex, handleIndex)].forEach(handle =>
      dispatch(setSegmentHandleStop({ ...handle, waypointId }))
    );
  };

/** Removes the waypoints the builder added for stops that no handle stops at any more. Off the undo stack, so undoing the edit that left them brings them back. */
export const removeUnusedStopWaypoints = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const { segments, waypoints } = getState().customRoute.route;
  const stopIds = getStopWaypointIds(segments.map(({ paths }) => paths));
  const unused = waypoints.filter(waypoint => waypoint.isAddedForStop && !stopIds.has(waypoint.id));
  if (unused.length) {
    dispatch(setRouteWaypoints(waypoints.filter(waypoint => !unused.includes(waypoint))));
  }
};
